import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import {
  TransferFilters,
  TransferRequest,
  TransferTableItem,
} from "../../types";
import {
  useTransferExport,
  useTransferFilters,
  useTransferRequests,
  useTransferRequestsWithTab,
} from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { useNavigate } from "react-router-dom";
import { FilterPanel } from "../filters";

const renderPriority = (value: string) => {
  if (value === "High") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
      </span>
    );
  }
  if (value === "Medium") {
    return (
      <span
        style={{
          color: "#d97706",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowUp size={16} style={{ color: "#d97706" }} /> Medium
      </span>
    );
  }
  if (value === "Low") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowDown size={16} style={{ color: "#2563eb" }} /> Low
      </span>
    );
  }
  if (value === "Critical") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> Critical
      </span>
    );
  }
  if (value === "Normal") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowDown size={16} style={{ color: "#2563eb" }} /> Normal
      </span>
    );
  }
  return value;
};

const columns: TableColumn<TransferTableItem>[] = [
  { key: "transferId", header: "Transfer ID", type: "text" },
  { key: "customerName", header: "Customer Name", type: "text" },
  { key: "accountNumber", header: "Account", type: "text" },
  { key: "transferType", header: "Transfer Type", type: "text" },
  { key: "requestedDate", header: "Requested Date", type: "text" },
  {
    key: "status",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) => {
        if (value === "pending") return "secondary";
        if (value === "approved") return "default";
        if (value === "completed") return "outline";
        if (value === "rejected") return "destructive";
        if (value === "cancelled") return "destructive";
        return "default";
      },
    },
  },
  { key: "assignedTo", header: "Created By", type: "text" },
  { key: "serviceOrderStatus", header: "SO Status", type: "badge" },
  {
    key: "priority",
    header: "Priority",
    type: "custom",
    render: (value) => renderPriority(value),
  },
];

export const CurrentList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
      },
      defaults: {
        page: 1,
        limit: 50,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    [],
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const exportFilters: TransferFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      requestType: "Transfer",
      activeTab: "Current",
      searchData: filters.search_data,
      status: filters.status?.length ? filters.status.join(",") : undefined,
      name: filters.name,
      // Note: No page and limit for export to get all data
    };
  }, [filters, remoteUtilityId]);

  const { data, isLoading, error } = useTransferRequestsWithTab(
    Number(remoteUtilityId),
    "Current",
    {
      remoteUtilityId: Number(remoteUtilityId),
      requestType: "Transfer",
      activeTab: "Current",
      page,
      limit: PAGE_SIZE,
      searchData: filters.search_data,
      status: filters.status?.length ? filters.status.join(",") : undefined,
      name: filters.name,
    },
  );

  const { exportCSV, isExporting } = useTransferExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const actions: TableAction<TransferTableItem>[] = [
    {
      label: "View",
      onClick: (item) => navigate(`/cx/transfer/current/${item.id}`),
    },
    // { label: 'Cancel', onClick: () => {} },
    // { label: 'Approve', onClick: () => {} },
    // { label: 'Reject', onClick: () => {} },
    // { label: 'Request Documents', onClick: () => {} },
    // { label: 'Print Details', onClick: () => {} },
  ];

  // Transform API data to match the table structure
  const transformedData: TransferTableItem[] =
    data?.results?.map((item: any) => ({
      id: item.id.toString(),
      transferId: item.requestNo || item.transferId,
      customerName: `${item.firstName} ${item.lastName}`,
      accountNumber: item.consumerNo || item.accountNumber,
      transferType: item?.utilitySupportRequest?.name,
      requestedDate: item.requestedDate || item.createdDate,
      status: item.statusDisplay?.toLowerCase() || item.status,
      assignedTo: item.createdUserRemoteName,
      financialStatus: item.financialStatus || "pending",
      priority: item.priorityLevelDisplay,
      serviceOrderStatus: item.serviceOrderStatus
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const currentTab = 2;

  return (
    <GenericTable
      data={transformedData}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: (page - 1) * PAGE_SIZE + 1,
        endItem: Math.min(page * PAGE_SIZE, totalItems),
      }}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      onExport={handleExport}
      exportLoading={isExporting}
      filterPanel={
        <FilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          tab={currentTab}
        />
      }
    />
  );
};
